import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';

import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = `${environment.apiBaseUrl}/auth`;
  private tokenKey = 'access_token';
  private currentUserSubject: BehaviorSubject<any>;
  public currentUser: Observable<any>;

  public currentOrgSubject = new BehaviorSubject<any>(null);
  public currentOrg$ = this.currentOrgSubject.asObservable();

  constructor(private http: HttpClient, private router: Router) {
    const token = localStorage.getItem(this.tokenKey);
    this.currentUserSubject = new BehaviorSubject<any>(token ? jwtDecode(token) : null);
    this.currentUser = this.currentUserSubject.asObservable();

    // Init Org
    const storedOrg = localStorage.getItem('current_org');
    if (storedOrg) {
      try {
        this.currentOrgSubject.next(JSON.parse(storedOrg));
      } catch (e) { }
    }
  }

  public get currentUserValue() {
    return this.currentUserSubject.value;
  }

  login(credentials: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/login`, credentials)
      .pipe(tap(response => {
        console.log('========================================');
        console.log('🔐 FRONTEND LOGIN RESPONSE');
        console.log('========================================');
        console.log('Full Response:', response);

        if (response.accessToken) {
          localStorage.setItem(this.tokenKey, response.accessToken);
          const decoded: any = jwtDecode(response.accessToken);

          // Enrich legacy user storage with accessibleOrgs if needed, or separate key
          // Enrich legacy user storage with accessibleOrgs if needed, or separate key
          if (response.accessibleOrgs) {
            console.log('📊 Processing', response.accessibleOrgs.length, 'organizations');
            localStorage.setItem('accessible_orgs', JSON.stringify(response.accessibleOrgs));

            // Store all dashboards from response for sidebar menu
            if (response.allDashboards && response.allDashboards.length > 0) {
              localStorage.setItem('plan_dashboards', JSON.stringify(response.allDashboards));
              console.log('✅ Stored', response.allDashboards.length, 'dashboards from response');
            } else {
              console.warn('⚠️ No allDashboards in response!');
            }

            // Logic for auto-selecting Org
            // If user has only 1 org, select it.
            // If user has multiple, user might need to choose (or we default to first).
            // CRITICAL: Always reset current org on new login to avoid stale state if not handled by logout.
            if (response.accessibleOrgs.length > 0) {
              console.log('🏢 Setting current org:', response.accessibleOrgs[0].name);
              this.setCurrentOrg(response.accessibleOrgs[0]);
            } else {
              // No accessible orgs? Clear current.
              localStorage.removeItem('current_org');
              this.currentOrgSubject.next(null);
            }
          }

          this.currentUserSubject.next(decoded);
          localStorage.setItem('user', JSON.stringify(decoded));
          console.log('========================================\n');
        }
      }));
  }

  // ... (rest of the file)

  getAccessibleOrgs() {
    const stored = localStorage.getItem('accessible_orgs');
    return stored ? JSON.parse(stored) : [];
  }

  setCurrentOrg(org: any) {
    localStorage.setItem('current_org', JSON.stringify(org));
    this.currentOrgSubject.next(org);
  }

  getCurrentOrg() {
    return this.currentOrgSubject.value;
  }

  logout() {
    this.http.post(`${this.apiUrl}/logout`, {}).subscribe({
      next: () => console.log('🛑 [Auth] Backend session ended'),
      error: (err) => console.error('⚠️ [Auth] Logout request failed', err)
    });

    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem('user');
    localStorage.removeItem('accessible_orgs');
    localStorage.removeItem('current_org');
    localStorage.removeItem('plan_dashboards');

    this.currentUserSubject.next(null);
    this.currentOrgSubject.next(null);

    this.router.navigate(['/auth/login']);
  }

  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    // TODO: Check expiration
    return !!token;
  }

  /**
   * Get user permissions based on:
   * 1. Current organization
   * 2. User's team in that organization
   * 3. Role assigned to that team
   */
  getUserPermissions(): string[] {
    const currentOrg = this.getCurrentOrg();
    const accessibleOrgs = this.getAccessibleOrgs();

    if (!currentOrg || !accessibleOrgs || accessibleOrgs.length === 0) {
      return [];
    }

    // Find the current org in accessible orgs (which includes team/role data)
    const orgData = accessibleOrgs.find((org: any) => org.id === currentOrg.id);

    if (!orgData) {
      return [];
    }

    // If user has Teams, get permissions from the team's boolean flags
    if (orgData.Teams && orgData.Teams.length > 0) {
      const allPermissions = new Set<string>();
      orgData.Teams.forEach((team: any) => {
        if (team.canViewDashboards) allPermissions.add('dashboards.view');
        if (team.canManageDashboards) allPermissions.add('dashboards.manage');
      });
      return Array.from(allPermissions);
    }

    // Fallback: check if user has direct role (for backward compatibility)
    const user = this.currentUserValue;
    if (user && user.role === 'superadmin') {
      return ['*']; // Superadmin has all permissions
    }

    return [];
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission: string): boolean {
    const permissions = this.getUserPermissions();

    // Superadmin wildcard
    if (permissions.includes('*')) {
      return true;
    }

    return permissions.includes(permission);
  }

  /**
   * Check if user can export data
   * Rules:
   * 1. If organization is in demo mode -> Block (regardless of permissions)
   * 2. If user has dashboards.manage permission -> Allow
   * 3. If user has only dashboards.view permission -> Block
   */
  canExport(): { allowed: boolean; reason?: string; title?: string } {
    const currentOrg = this.getCurrentOrg();
    const permissions = this.getUserPermissions();

    // 1. Check demo mode first (Highest Priority)
    if (currentOrg?.isDemo) {
      return {
        allowed: false,
        reason: 'You are in Demo Mode. Exporting data is not allowed.',
        title: 'Demo Mode Restrictions'
      };
    }

    // 2. Check if user has manage permission (or superadmin)
    if (permissions.includes('*') || permissions.includes('dashboards.manage')) {
      return { allowed: true };
    }

    // 3. Check if user has only view permission (or no manage permission)
    if (permissions.includes('dashboards.view')) {
      return {
        allowed: false,
        reason: 'You do not have permission to export data. Please contact your administrator.',
        title: 'Permission Restricted'
      };
    }

    // Default fallback (no relevant permissions)
    return {
      allowed: false,
      reason: 'You do not have permission to export data. Please contact your administrator.',
      title: 'Permission Restricted'
    };
  }
}
